import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles, Loader } from 'lucide-react';
import { useAuth } from '../context/authContext';
import DynamicLayout from '../components/DynamicLayout';
import NoIndexHelmet from '../components/NoIndexHelmet';

const ForgotPasswordPage = () => {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await resetPassword(email);
      setMessage('Password reset email sent! Check your inbox and spam folder.');
    } catch (error) {
      if (error?.code === 'auth/user-not-found') {
        setError('We couldn\'t find an account with that email. Try another or register.');
      } else if (error?.code === 'auth/invalid-email') {
        setError('That email doesn\'t look right. Please enter a valid address.');
      } else {
        setError('Something went wrong sending the email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DynamicLayout>
      <NoIndexHelmet title="Forgot Password | Saturnalia" />
      <section className="forgot-password-page">
        <div className="forgot-password-hero" />
        <div className="forgot-password-overlay">
          <div className="forgot-password-card lightning-border">
            <button
              type="button"
              className="forgot-password-back button-tertiary"
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <ArrowLeft size={18} />
              <span>Back</span>
            </button>

            <div className="forgot-password-header">
              <div className="forgot-password-icon">
                <Sparkles size={32} />
              </div>
              <h1>Reset Your Password</h1>
              <p>
                Enter the email associated with your account and we&apos;ll send over a sacred reset link.
              </p>
            </div>

            <form className="forgot-password-form" onSubmit={handleSubmit}>
              <label htmlFor="email" className="forgot-password-label">
                Email Address
              </label>
              <div className="forgot-password-input">
                <Sparkles size={18} />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <button className="forgot-password-submit" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader className="spinner" size={18} />
                    Sending
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Reset Link
                  </>
                )}
              </button>
            </form>

            {(message || error) && (
              <div className={`forgot-password-alert auth-alert ${message ? 'is-success' : 'is-error'}`} role="alert">
                <div className="auth-alert__icon" aria-hidden="true">
                  {message ? '✓' : '!'}
                </div>
                <div className="auth-alert__content">
                  <div className="auth-alert__title">
                    {message ? 'Email sent' : 'Something went wrong'}
                  </div>
                  <div className="auth-alert__text">
                    {message ? message : error}
                  </div>
                </div>
              </div>
            )}

            <div className="forgot-password-footer">
              <span>Remember your password?</span>
              <Link to="/login">Return to login</Link>
            </div>
          </div>
        </div>
      </section>
    </DynamicLayout>
  );
};

export default ForgotPasswordPage;
